import { useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import { submitBugReport } from "../api/bugReports";
import Button from "./ds/Button";
import Icon from "./ds/Icon";
import IconButton, { STATE_LAYER } from "./ds/IconButton";
import Modal from "./ds/Modal";
import TextField from "./ds/TextField";
import TextView from "./ds/TextView";

const MAX_FILES = 10;
const MAX_IMAGE_MB = 10;
const MAX_VIDEO_MB = 100;

const BugReportModal = ({ isOpen, onClose, isDark, setToast }) => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  /** @type {{ file: File, preview: string, kind: 'image'|'video' }[]} */
  const [items, setItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    items.forEach((i) => {
      if (i.preview?.startsWith("blob:")) URL.revokeObjectURL(i.preview);
    });
    setTitle("");
    setDescription("");
    setItems([]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const addFiles = (fileList) => {
    const next = [...items];
    for (const file of Array.from(fileList || [])) {
      if (next.length >= MAX_FILES) break;
      const isImg = file.type.startsWith("image/");
      const isVid = file.type.startsWith("video/");
      if (!isImg && !isVid) continue;
      const maxBytes = isVid ? MAX_VIDEO_MB * 1024 * 1024 : MAX_IMAGE_MB * 1024 * 1024;
      if (file.size > maxBytes) {
        setToast?.(t("bugReport.fileTooLarge"));
        continue;
      }
      const preview = URL.createObjectURL(file);
      next.push({
        file,
        preview,
        kind: isVid ? "video" : "image",
      });
    }
    setItems(next);
  };

  const removeAt = (index) => {
    setItems((prev) => {
      const copy = [...prev];
      const [removed] = copy.splice(index, 1);
      if (removed?.preview?.startsWith("blob:")) URL.revokeObjectURL(removed.preview);
      return copy;
    });
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      setToast?.(t("bugReport.signInRequired"));
      return;
    }
    const tTrim = title.trim();
    const dTrim = description.trim();
    if (tTrim.length < 3) {
      setToast?.(t("bugReport.titleTooShort"));
      return;
    }
    if (dTrim.length < 10) {
      setToast?.(t("bugReport.descriptionTooShort"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitBugReport({
        title: tTrim,
        description: dTrim,
        files: items.map((i) => i.file),
      });
      if (res.success) {
        setToast?.(t("bugReport.submitted"));
        handleClose();
      } else {
        throw new Error(res.message || "Failed");
      }
    } catch (err) {
      setToast?.(err?.response?.data?.message || err?.message || t("bugReport.submitFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const full = submitting || items.length >= MAX_FILES;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={t("bugReport.modalTitle")}
      closeLabel={t("common.close")}
      isDark={isDark}
      width="min-[600px]:w-[560px]"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-space-20">
        <div className="flex flex-col gap-space-16">
          {!isAuthenticated && <p className="text-subheadline text-label-secondary">{t("bugReport.signInHint")}</p>}
          <TextField
            label={t("bugReport.titleLabel")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder={t("bugReport.titlePlaceholder")}
            disabled={submitting}
          />
          <TextView
            label={t("bugReport.descriptionLabel")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={10000}
            placeholder={t("bugReport.descriptionPlaceholder")}
            disabled={submitting}
          />
          <div className="flex flex-col gap-space-8">
            <p className="text-footnote-semibold text-label-secondary">{t("bugReport.attachmentsLabel")}</p>
            <input
              ref={inputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              className="hidden"
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
              disabled={full}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (!full) addFiles(e.dataTransfer.files);
              }}
              disabled={full}
              className={`relative flex flex-col items-center gap-space-8 rounded-radius-md border border-separator-default p-space-20 text-center disabled:cursor-not-allowed disabled:opacity-40 ${STATE_LAYER}`}
            >
              <span className="text-accent-text">
                <Icon name="upload" size={24} />
              </span>
              <span className="text-subheadline-semibold text-accent-text">{t("bugReport.addFiles")}</span>
              <span className="text-footnote text-label-tertiary">{t("bugReport.attachmentsHint")}</span>
            </button>

            {items.length > 0 && (
              <ul className="mt-space-4 grid grid-cols-2 gap-space-8 min-[600px]:grid-cols-3">
                {items.map((it, idx) => (
                  <li key={`${it.file.name}-${idx}`} className="relative">
                    {it.kind === "image" ? (
                      <img src={it.preview} alt="" className="h-24 w-full rounded-radius-sm object-cover" />
                    ) : (
                      <video src={it.preview} className="h-24 w-full rounded-radius-sm object-cover" muted playsInline />
                    )}
                    <IconButton
                      label={t("common.remove")}
                      className="!absolute right-space-4 top-space-4"
                      onClick={() => removeAt(idx)}
                    >
                      <Icon name="trash" size={18} />
                    </IconButton>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-space-12">
          <Button variant="gray" onClick={handleClose} disabled={submitting}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={submitting} disabled={!isAuthenticated}>
            {submitting ? t("common.loading") : t("bugReport.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default BugReportModal;
